import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { OrderService } from '../services/order-service';
import httpClient from '../services/http-client';
import { PagedResult, PendingApprovalDto } from '../types';
import { useDivisionContext } from '../contexts/division-context';

// Create an order service instance using the default HTTP client
const orderService = new OrderService(httpClient);

export interface PendingApprovalsFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  submitterId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/**
 * Hook to get pending approvals with server-side pagination, filtering, and sorting
 */
export function usePendingApprovals(filters?: PendingApprovalsFilters) {
  const { isAuthenticated } = useAuth();
  const { selectedDivisionId } = useDivisionContext();
  
  const query = useQuery({
    queryKey: ['pending-approvals', selectedDivisionId, filters],
    queryFn: async (): Promise<PagedResult<PendingApprovalDto>> => {
      if (!selectedDivisionId) {
        throw new Error('No division ID selected');
      }
      return orderService.getPendingApprovals(selectedDivisionId, filters);
    },
    enabled: isAuthenticated && !!selectedDivisionId,
    staleTime: 15 * 1000, // 15 seconds for approval data
    placeholderData: (previousData) => previousData, // Keep previous data while fetching to prevent flickering
    retry: (failureCount, error) => {
      // Don't retry if there's no division ID
      if (error?.message?.includes('No division ID')) {
        return false;
      }
      return failureCount < 3;
    }
  });

  return {
    ...query,
    divisionId: selectedDivisionId
  };
}
